import Editor from '@monaco-editor/react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, PlayIcon, StopCircle, CreditCard } from 'lucide-react';
import { cancelTask, runTaskSSE, startProject } from '@/service/serverless-gpu';
import { useParams } from 'react-router-dom';
import { RouteParams } from '@/router/type';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { toastSomethingError } from '@/components/ui/toast-content';
import { toBase64 } from '@/utils';
import { defaultCode } from './default';
import { useServerlessStore } from '@/store/serverless';
import { useGetUserBalance } from '@/service/payment/hook';
import { useQuickTopup } from '@/hooks/useQuickTopup';
import { QuickTopupDialog } from '@/components/quick-topup/QuickTopupDialog';
import {
  parseSSEStream,
  SSETaskIdData,
  SSEStatusData,
  SSEProgressData,
  SSEOutputData,
  SSECompleteData,
  SSEErrorData,
} from '@/utils/sse';

const ServerlessGPUPlaygroundRun = () => {
  const { projectId } = useParams<RouteParams>();
  const { token } = useServerlessStore();
  const [code, setCode] = useState<string>(defaultCode);
  const [fileName, setFileName] = useState<string>('quick-run');
  const editorRef = useRef(null);
  const outputRef = useRef<HTMLPreElement>(null);

  // Credit balance check
  const { data: creditData } = useGetUserBalance();
  const hasInsufficientBalance = (creditData?.balance ?? 0) < 0.5;
  const { isOpen, setIsOpen, openTopup, onSuccess } = useQuickTopup();

  // SSE state
  const [taskId, setTaskId] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('idle');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [progress, setProgress] = useState<number>(0);
  const [output, setOutput] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const [duration, setDuration] = useState<number | null>(null);
  const [coldStartTime, setColdStartTime] = useState<number | null>(null);
  const startTimestampRef = useRef<number | null>(null);

  function handleEditorDidMount(editor: any, monaco: any) {
    editorRef.current = editor;
  }

  // Auto-scroll output to bottom when new output arrives
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  const handleRun = async () => {
    if (!projectId || !token) {
      toastSomethingError('Missing project ID or token');
      return;
    }

    // Reset state
    setTaskId(null);
    setStatus('starting');
    setStatusMessage('Initializing task...');
    setProgress(0);
    setOutput([]);
    setError(null);
    setIsRunning(true);
    setDuration(null);
    setColdStartTime(null);
    startTimestampRef.current = Date.now();

    try {
      // Start project first
      await startProject({
        project_id: projectId,
        config: {
          mode: 'realtime',
        },
        requirements_content: '',
      });

      // Make SSE request
      const response = await runTaskSSE(
        {
          files: [
            {
              name: fileName + '.py',
              content: toBase64(code),
            },
          ],
          name: fileName + '.py',
          project_id: projectId,
        },
        token
      );

      if (!response.body) {
        throw new Error('No response body');
      }

      const reader = response.body.getReader();

      // Process SSE events
      for await (const event of parseSSEStream(reader)) {
        switch (event.type) {
          case 'task_id': {
            const data = event.data as SSETaskIdData;
            setTaskId(data.task_id);
            setStatus('running');
            setStatusMessage('Task created');
            // Calculate cold start time when task starts
            if (startTimestampRef.current) {
              setColdStartTime((Date.now() - startTimestampRef.current) / 1000);
            }
            break;
          }
          case 'status': {
            const data = event.data as SSEStatusData;
            setStatus(data.status);
            setStatusMessage(data.message);
            break;
          }
          case 'progress': {
            const data = event.data as SSEProgressData;
            setProgress(data.progress);
            setStatusMessage(data.message);
            break;
          }
          case 'output': {
            const data = event.data as SSEOutputData;
            setOutput((prev) => [...prev, data.output]);
            break;
          }
          case 'complete': {
            const data = event.data as SSECompleteData;
            setStatus('completed');
            setStatusMessage(data.finish_reason);
            setDuration(data.duration);
            setIsRunning(false);
            toast.success('Task completed successfully');
            break;
          }
          case 'error': {
            const data = event.data as SSEErrorData;
            setStatus('error');
            setError(data.error);
            setStatusMessage(data.message || data.error);
            setIsRunning(false);
            toastSomethingError(data.error);
            break;
          }
        }
      }

      // If we exit the loop without complete/error, mark as done
      if (isRunning) {
        setIsRunning(false);
      }
    } catch (err: any) {
      setStatus('error');
      setError(err.message || 'Unknown error');
      setStatusMessage(err.message || 'Unknown error');
      setIsRunning(false);
      toastSomethingError(err.message);
    }
  };

  const handleCancel = async () => {
    if (!taskId || !token) return;

    setIsCancelling(true);
    try {
      await cancelTask(taskId, token);
      toast.success('Cancel request sent');
    } catch (err: any) {
      toastSomethingError(err.message);
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Input
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
          />
          <span>.py</span>
        </div>
        <div className="flex items-center gap-2">
          {hasInsufficientBalance ? (
            <Button
              variant={'default'}
              startIcon={<CreditCard className="h-4 w-4" />}
              onClick={openTopup}
            >
              Topup & Run
            </Button>
          ) : (
            <Button
              variant={'default'}
              startIcon={
                isRunning ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <PlayIcon className="h-4 w-4" />
                )
              }
              onClick={handleRun}
              disabled={isRunning}
            >
              Run
            </Button>
          )}
          {isRunning && taskId && (
            <Button
              variant="destructive"
              startIcon={
                isCancelling ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <StopCircle className="h-4 w-4" />
                )
              }
              onClick={handleCancel}
              disabled={isCancelling}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>
      <div className="h-[500px] w-full overflow-hidden">
        <Editor
          height="100%"
          width="100%"
          defaultLanguage="python"
          value={code}
          onMount={handleEditorDidMount}
          onChange={(value) => {
            setCode(value!);
          }}
          options={{
            minimap: {
              enabled: false,
            },
            padding: {
              top: 10,
            },
          }}
          className="border-2"
        />
      </div>

      {/* Task Info */}
      {taskId && (
        <div className="flex items-center gap-4">
          <p className="text-sm text-gray-500">Task ID: {taskId}</p>
          <p className="text-sm text-gray-500">
            Status: <span className="font-medium">{status}</span>
            {statusMessage && ` - ${statusMessage}`}
          </p>
          {typeof coldStartTime === 'number' && (
            <p className="text-sm text-gray-500">
              Cold Start: {coldStartTime.toFixed(2)}s
            </p>
          )}
          {typeof duration === 'number' && (
            <p className="text-sm text-gray-500">
              Duration: {duration.toFixed(2)}s
            </p>
          )}
        </div>
      )}

      {/* Progress Bar */}
      {progress > 0 && progress < 100 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-800">Error</p>
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Output</p>
          <pre
            ref={outputRef}
            className="max-h-[400px] overflow-auto rounded-lg bg-[#1e1e1e] p-4 font-mono text-sm text-white whitespace-pre-wrap"
          >
            {output.join('\n')}
          </pre>
        </div>
      )}

      <QuickTopupDialog open={isOpen} onOpenChange={setIsOpen} onSuccess={onSuccess} />
    </div>
  );
};

export default ServerlessGPUPlaygroundRun;
